import { useCallback, useEffect, useRef, useState } from 'react';
import { productDraftsApi } from '@/api/productDraftsApi';
import type { ProductFormMode, ProductFormState, DraftSaveStatus } from './types';
import {
  createEmptyProductFormState,
  draftPayloadToState,
  stateToDraftPayload,
} from './types';

const AUTOSAVE_MS = 2000;

interface UseProductFormDraftOptions {
  draftId?: string | null;
  enabled: boolean;
  formMode: ProductFormMode;
  currentStep: string;
  onDraftIdChange?: (id: string) => void;
  buildPayload?: (state: ProductFormState) => ReturnType<typeof stateToDraftPayload>;
  onDraftLoaded?: (draft: { currentStep?: string | null; formMode?: ProductFormMode | null }) => void;
}

export function useProductFormDraft({
  draftId: initialDraftId,
  enabled,
  formMode,
  currentStep,
  onDraftIdChange,
  buildPayload,
  onDraftLoaded,
}: UseProductFormDraftOptions) {
  const [state, setState] = useState<ProductFormState>(createEmptyProductFormState());
  const [draftId, setDraftId] = useState<string | null>(initialDraftId ?? null);
  const [saveStatus, setSaveStatus] = useState<DraftSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef(state);
  const skipNextSaveRef = useRef(false);
  const onDraftLoadedRef = useRef(onDraftLoaded);

  stateRef.current = state;
  onDraftLoadedRef.current = onDraftLoaded;

  useEffect(() => {
    setDraftId(initialDraftId ?? null);
  }, [initialDraftId]);

  useEffect(() => {
    if (!enabled || !initialDraftId) return;

    let cancelled = false;
    setIsLoadingDraft(true);

    productDraftsApi
      .getById(initialDraftId)
      .then((draft) => {
        if (cancelled) return;
        skipNextSaveRef.current = true;
        setState(draftPayloadToState(draft.payload));
        setDraftId(draft.id);
        onDraftLoadedRef.current?.({
          currentStep: draft.currentStep,
          formMode: draft.formMode,
        });
      })
      .catch(() => {
        if (!cancelled) setSaveStatus('error');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDraft(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, initialDraftId]);

  const persistDraft = useCallback(async () => {
    if (!enabled) return;

    const payload = buildPayload
      ? buildPayload(stateRef.current)
      : stateToDraftPayload(stateRef.current);
    const label = stateRef.current.name.trim() || 'Untitled product draft';

    setSaveStatus('saving');

    try {
      if (draftId) {
        await productDraftsApi.update(draftId, {
          payload,
          currentStep,
          formMode,
          label,
        });
      } else {
        const created = await productDraftsApi.create({
          payload,
          currentStep,
          formMode,
          label,
        });
        setDraftId(created.id);
        onDraftIdChange?.(created.id);
      }
      setSaveStatus('saved');
      setLastSavedAt(new Date());
    } catch {
      setSaveStatus('error');
    }
  }, [enabled, draftId, currentStep, formMode, onDraftIdChange, buildPayload]);

  useEffect(() => {
    if (!enabled) return;

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    setSaveStatus((prev) => (prev === 'saving' ? prev : 'pending'));

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void persistDraft();
    }, AUTOSAVE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [state, enabled, formMode, currentStep, persistDraft]);

  const updateState = useCallback(
    (updater: ProductFormState | ((prev: ProductFormState) => ProductFormState)) => {
      setState((prev) => (typeof updater === 'function' ? updater(prev) : updater));
    },
    []
  );

  const resetState = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setState(createEmptyProductFormState());
    setDraftId(null);
    setSaveStatus('idle');
    setLastSavedAt(null);
  }, []);

  const saveNow = useCallback(async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    await persistDraft();
  }, [persistDraft]);

  return {
    state,
    setState,
    updateState,
    draftId,
    saveStatus,
    lastSavedAt,
    isLoadingDraft,
    resetState,
    saveNow,
  };
}
